import { ReactElement, useMemo } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import { RootState } from '../../redux/store';
import { eventActions } from '../../redux/slices/event.slice';
import { ITeam } from '../../types/@team';
import { EEventCreateSteps } from '../../types/@event';
import { IOrganisationEventItem } from '../../types/@organisation';

interface IProps {
  setStep: React.Dispatch<React.SetStateAction<number>>;
  organisationScoped?: boolean;
  onCreateNewStyle?: () => void;
}

const getLatestEvent = (events: IOrganisationEventItem[]): IOrganisationEventItem | null => {
  if (!events.length) {
    return null;
  }

  return [...events].sort((left, right) => {
    const leftValue = Number(left.sortValue || left.eventStartTime || 0);
    const rightValue = Number(right.sortValue || right.eventStartTime || 0);
    return rightValue - leftValue;
  })[0] || null;
};

const EventCreateTeam = ({ setStep, organisationScoped = false, onCreateNewStyle }: IProps): ReactElement => {
  const dispatch = useDispatch();
  const teams = useSelector((state: RootState) => state.teams.all);
  const organisationEvents = useSelector((state: RootState) => state.organisations.events);
  const selectedOrganisation = useSelector((state: RootState) => state.events.eventCreate.organisation);
  const formValues = useSelector((state: RootState) => state.events.eventCreate.eventForm);

  const scopedTeams = useMemo(() => {
    const organisationId = String(selectedOrganisation?._id || '').trim();
    const organisationEventsMatch =
      organisationScoped &&
      organisationEvents &&
      String(organisationEvents.organisationId || '').trim() === organisationId;

    if (organisationEventsMatch) {
      return organisationEvents!.teams.map((team): { team: ITeam; latestEvent: IOrganisationEventItem | null } => ({
        team: {
          _id: team.teamId,
          name: team.teamName,
          twitterId: '',
          instagramId: '',
        },
        latestEvent: getLatestEvent(team.events),
      }));
    }

    return teams.map((team) => ({
      team,
      latestEvent: null,
    }));
  }, [organisationEvents, organisationScoped, selectedOrganisation?._id, teams]);

  const handleSelect = (team: ITeam, teamHistory: IOrganisationEventItem | null): void => {
    dispatch(eventActions.selectedTeam(team));

    if (teamHistory) {
      dispatch(
        eventActions.eventCreateForm({
          ...formValues,
          twitterId: teamHistory.twitterId || formValues.twitterId,
          instagramId: teamHistory.instagramId || formValues.instagramId,
          tiktokId: teamHistory.tiktokId || formValues.tiktokId,
        })
      );
    }

    setStep(EEventCreateSteps.event);
  };

  const handleBack = (): void => {
    if (organisationScoped) {
      setStep(EEventCreateSteps.type);
      return;
    }

    setStep(EEventCreateSteps.organisation);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '920px', margin: '0 auto', padding: '48px 24px' }}>
      <Typography sx={{ fontSize: '32px', fontWeight: 700, marginBottom: '8px' }}>
        Select team
      </Typography>

      <Typography sx={{ fontSize: '16px', color: '#666', marginBottom: '24px' }}>
        Only teams inside this organisation are shown here. Team social IDs are prefilled from the latest event for that team.
      </Typography>

      <Box sx={{ display: 'grid', gap: '16px' }}>
        {scopedTeams.map(({ team, latestEvent }) => (
          <Box
            key={team._id || team.name}
            sx={{
              border: '1px solid #ddd',
              borderRadius: '12px',
              padding: '20px',
              cursor: 'pointer',
            }}
            onClick={() => handleSelect(team, latestEvent)}
          >
            <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>{team.name}</Typography>
            {latestEvent ? (
              <Typography sx={{ fontSize: '14px', color: '#6b7c8f', mt: 0.75 }}>
                Prefill from latest event: {latestEvent.name}
              </Typography>
            ) : null}
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', marginTop: '32px' }}>
        <Button onClick={handleBack}>Back</Button>
        {organisationScoped && onCreateNewStyle ? (
          <Button variant="outlined" onClick={onCreateNewStyle}>
            New style event
          </Button>
        ) : <Box />}
      </Box>
    </Box>
  );
};

export default EventCreateTeam;
